import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Users, Shield, ClipboardList, ShoppingCart, Boxes,
  Package, Tags, Award, CalendarClock, CreditCard, HandCoins, Percent, MessageSquare,
  Building2, BarChart3, Globe, ScrollText, Settings, LogOut, Menu, X, Store,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import Brand from "@/components/public/Brand";
import { ThemeToggle } from "@/components/public/PublicLayout";

const MENU = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, module: "dashboard", end: true },
  { to: "/admin/requests", label: "Quote Requests", icon: FileText, module: "requests" },
  { to: "/admin/customers", label: "Customers", icon: Users, module: "customers" },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart, module: "orders" },
  { to: "/admin/products", label: "Products", icon: Package, module: "products" },
  { to: "/admin/categories", label: "Categories", icon: Tags, module: "categories" },
  { to: "/admin/brands", label: "Brands", icon: Award, module: "brands" },
  { to: "/admin/plans", label: "Installment Plans", icon: CalendarClock, module: "plans" },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, module: "payments" },
  { to: "/admin/recovery", label: "Recovery", icon: HandCoins, module: "recovery" },
  { to: "/admin/deals", label: "Deals", icon: Percent, module: "deals" },
  { to: "/admin/complaints", label: "Complaints", icon: MessageSquare, module: "complaints" },
  { to: "/admin/branches", label: "Branch Management", icon: Building2, module: "branches" },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, module: "reports" },
  { to: "/admin/cms", label: "Website / CMS", icon: Globe, module: "cms" },
  { to: "/admin/users", label: "User Management", icon: Users, module: "users" },
  { to: "/admin/roles", label: "Role Management", icon: Shield, module: "roles" },
  { to: "/admin/audit", label: "Audit Logs", icon: ScrollText, module: "audit" },
  { to: "/admin/settings", label: "Settings", icon: Settings, module: "settings" },
];

export default function AdminLayout({ children, title, actions }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { can, perms } = usePermissions();
  const [open, setOpen] = useState(false);

  const visible = MENU.filter((m) => can(m.module, "view"));
  const active = (m) => (m.end ? location.pathname === m.to : location.pathname.startsWith(m.to));

  const Sidebar = (
    <div className="flex h-full flex-col">
      <Link to="/admin" className="flex items-center gap-2 border-b border-border px-5 py-4">
        <Brand height={34} textClass="font-display text-sm font-extrabold leading-none" />
      </Link>
      <nav className="hide-scrollbar flex-1 space-y-0.5 overflow-y-auto px-3 py-3">
        {visible.map((m) => (
          <Link key={m.to} to={m.to} onClick={() => setOpen(false)} data-testid={`admin-nav-${m.module}`}
            className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${active(m) ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
            <m.icon className="h-4 w-4 shrink-0" />{m.label}
          </Link>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <Link to="/" className="mb-1 flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary"><Store className="h-4 w-4" />View Store</Link>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary"><LogOut className="h-4 w-4" />Sign Out</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-secondary/30">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-border bg-card lg:block">{Sidebar}</aside>
      {open && <div className="fixed inset-0 z-50 lg:hidden"><div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} /><div className="absolute inset-y-0 left-0 w-64 bg-card">{Sidebar}</div></div>}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-border bg-background/80 px-4 py-3 glass sm:px-6">
          <button className="grid h-9 w-9 place-items-center rounded-lg border border-border lg:hidden" onClick={() => setOpen(true)}><Menu className="h-4 w-4" /></button>
          <h1 className="font-display text-lg font-bold tracking-tight sm:text-xl" data-testid="admin-page-title">{title}</h1>
          <div className="ml-auto flex items-center gap-2">
            {actions}
            <ThemeToggle />
            <div className="hidden items-center gap-2 rounded-full border border-border px-2 py-1 pr-3 sm:flex">
              <span className="grid h-7 w-7 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{(user?.name || "A")[0]}</span>
              <div className="leading-tight"><p className="text-xs font-semibold">{user?.name}</p><p className="text-[10px] capitalize text-muted-foreground">{(perms?.role || user?.role || "").replace(/_/g, " ")}</p></div>
            </div>
          </div>
        </header>
        <main className="p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
}
